// Edge-compatible GraphQL client
import { useBooking } from "@/contexts/BookingContext";
import { useState, useEffect, useCallback } from "react";

const API_URL =
  import.meta.env.VITE_APOLLO_CLIENT_URI ||
  "https://api-staging.withsplice.com/graphql";

export interface GraphQLError {
  message: string;
  extensions?: any;
  locations?: Array<{
    line: number;
    column: number;
  }>;
  path?: Array<string | number>;
}

export interface GraphQLResponse<T = any> {
  data?: T;
  errors?: GraphQLError[];
}

// Core fetch function for GraphQL requests
export async function graphqlFetch<T = any>(
  query: string,
  variables?: Record<string, any>,
  options?: RequestInit
): Promise<GraphQLResponse<T>> {
  try {
    const token =
      typeof window !== "undefined"
        ? localStorage.getItem("customer-token")
        : null;

    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        query,
        variables,
      }),
      ...options,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("GraphQL HTTP Error:", errorText);

      // Handle authentication errors
      if (response.status === 401) {
        console.warn("Authentication error detected, clearing token");
        if (typeof window !== "undefined") {
          localStorage.removeItem("customer-token");
        }
      }

      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const json = await response.json();

    if (json.errors && json.errors.length > 0) {
      console.error("GraphQL Errors:", json.errors);

      // Handle authentication errors in GraphQL response
      const authError = json.errors.find(
        (error: any) =>
          error.extensions?.code === "UNAUTHENTICATED" ||
          error.message.includes("Unauthorized")
      );

      if (authError) {
        console.warn("GraphQL authentication error detected, clearing token");
        if (typeof window !== "undefined") {
          localStorage.removeItem("customer-token");
        }
      }
    }

    return json;
  } catch (error) {
    console.error("GraphQL fetch error:", error);
    throw error;
  }
}

// Hook for GraphQL queries
export function useQuery<T = any>(
  query: string,
  variables?: Record<string, any>,
  options?: {
    skip?: boolean;
    fetchPolicy?: "cache-first" | "network-only";
    onCompleted?: (data: T) => void;
    onError?: (error: Error) => void;
  }
) {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(!options?.skip);
  const [error, setError] = useState<Error | undefined>(undefined);

  const fetchData = useCallback(async () => {
    if (options?.skip) return;

    setLoading(true);
    setError(undefined);

    try {
      const response = await graphqlFetch<T>(query, variables);

      if (response.errors && response.errors.length > 0) {
        const error = new Error(response.errors[0].message);
        setError(error);
        options?.onError?.(error);
      } else if (response.data) {
        setData(response.data);
        options?.onCompleted?.(response.data);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Unknown error");
      setError(error);
      options?.onError?.(error);
    } finally {
      setLoading(false);
    }
  }, [query, JSON.stringify(variables), options?.skip]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetch = useCallback(
    async (newVariables?: Record<string, any>) => {
      const vars = newVariables || variables;
      setLoading(true);
      setError(undefined);

      try {
        const response = await graphqlFetch<T>(query, vars);

        if (response.errors && response.errors.length > 0) {
          const error = new Error(response.errors[0].message);
          setError(error);
          options?.onError?.(error);
        } else if (response.data) {
          setData(response.data);
          options?.onCompleted?.(response.data);
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Unknown error");
        setError(error);
        options?.onError?.(error);
      } finally {
        setLoading(false);
      }
    },
    [query, variables, options]
  );

  return { data, loading, error, refetch };
}

// Hook for GraphQL mutations
export function useMutation<
  T = any,
  V extends Record<string, any> = Record<string, any>
>(
  mutation: string,
  options?: {
    onCompleted?: (data: T) => void;
    onError?: (error: Error) => void;
  }
) {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const { addToast } = useBooking();

  const mutate = useCallback(
    async (variables?: V) => {
      setLoading(true);
      setError(undefined);

      try {
        const response = await graphqlFetch<T>(mutation, {
          ...(variables as Record<string, any>),
        });

        if (response.errors && response.errors.length > 0) {
          const error = new Error(response.errors[0].message);
          setError(error);
          options?.onError?.(error);
          addToast({
            message: error.message,
            variant: "error",
          });
          return { data: undefined, error };
        } else if (response.data) {
          setData(response.data);
          options?.onCompleted?.(response.data);
          return { data: response.data, error: undefined };
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Unknown error");
        setError(error);
        options?.onError?.(error);
        return { data: undefined, error };
      } finally {
        setLoading(false);
      }

      return { data: undefined, error: undefined };
    },
    [mutation, options]
  );

  return [mutate, { data, loading, error }] as const;
}

// Hook for lazy queries (manual execution)
export function useLazyQuery<T = any>(
  query: string,
  options?: {
    onCompleted?: (data: T) => void;
    onError?: (error: Error) => void;
  }
) {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [called, setCalled] = useState(false);

  const execute = useCallback(
    async (variables?: Record<string, any>) => {
      setCalled(true);
      setLoading(true);
      setError(undefined);

      try {
        const response = await graphqlFetch<T>(query, variables);

        if (response.errors && response.errors.length > 0) {
          const error = new Error(response.errors[0].message);
          setError(error);
          options?.onError?.(error);
          return { data: undefined, error };
        } else if (response.data) {
          setData(response.data);
          options?.onCompleted?.(response.data);
          return { data: response.data, error: undefined };
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Unknown error");
        setError(error);
        options?.onError?.(error);
        return { data: undefined, error };
      } finally {
        setLoading(false);
      }

      return { data: undefined, error: undefined };
    },
    [query, options]
  );

  return [execute, { data, loading, error, called }] as const;
}
